import { useEffect, useRef, useState } from "react";
import { IANATimezones } from "~/utils/datetime";
import { darkTheme, styled } from "../style/stitches.config";

export const TimezoneMenu = ({
  timezone,
  setTimezone,
  style,
}: {
  timezone: string;
  setTimezone: Function;
  style?: Object;
}) => {
  return (
    <MoonlightDropdownMenu
      selected={timezone}
      setSelected={setTimezone}
      style={style}
      dropdownListContent={IANATimezones}
    ></MoonlightDropdownMenu>
  );
};

export const MoonlightDropdownMenu = ({
  selected,
  setSelected,
  style,
  dropdownListContent,
  align,
  fullWidth,
  searchPlaceholder,
}: {
  selected: string;
  setSelected: Function;
  style?: Object;
  dropdownListContent: string[];
  align?: "center" | "left";
  fullWidth?: boolean;
  // Hint text for the search input, which is otherwise blank on open and gives
  // no indication it filters the list. Callers pass an already-localized string.
  searchPlaceholder?: string;
}) => {
  const [currentSearch, setCurrentSearch] = useState("");
  const containerRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  // Close the menu when clicking anywhere outside it (the open state lives as
  // an `.open` class on the container, so we just strip it).
  useEffect(() => {
    const handlePointerDown = (e: MouseEvent) => {
      const container = containerRef.current;
      if (container && !container.contains(e.target as Node)) {
        container.classList.remove("open");
      }
    };
    document.addEventListener("mousedown", handlePointerDown);
    return () => document.removeEventListener("mousedown", handlePointerDown);
  }, []);

  const classNames = ["menu"];
  if (align === "left") classNames.push("align-left");
  if (fullWidth) classNames.push("full-width");

  return (
    <StyledTimezoneMenu
      key="timezone-menu-container"
      ref={containerRef}
      id="tz-menu"
      className={classNames.join(" ")}
      style={style}
      onClick={() => {
        const container = containerRef.current;
        if (!container) return;
        const isOpen = container.classList.toggle("open");
        // The input is only unhidden once `.open` is set, so focus it after the
        // toggle. (`autoFocus` can't do this — the input stays mounted and is
        // merely hidden by CSS, so it never remounts on open.)
        if (isOpen) inputRef.current?.focus();
      }}
    >
      {selected}
      <img className="dropdown-caret" src="/images/next-arrow.svg" alt="" />
      <div className="tz-input-container">
        <input
          type="text"
          key="tz-search"
          id="tz-search"
          ref={inputRef}
          placeholder={searchPlaceholder}
          onClick={(e) => e.stopPropagation()}
          onChange={(e) => {
            setCurrentSearch(e.target.value.trim());
          }}
        ></input>
      </div>
      <div className="menu">
        <div className="inner-menu">
          {dropdownListContent
            .filter((tz) =>
              tz.toLowerCase().includes(currentSearch.toLowerCase())
            )
            .map((tz) => {
              return (
                <div
                  //@ts-ignore
                  key={"timezone-" + tz}
                  className="menu-item"
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelected(tz);
                    (
                      e.target as HTMLElement
                    ).parentElement!.parentElement!.parentElement!.classList.remove(
                      "open"
                    );
                  }}
                >
                  {tz}
                </div>
              );
            })}
        </div>
      </div>
    </StyledTimezoneMenu>
  );
};

const StyledTimezoneMenu = styled("div", {
  fontSize: "12px",
  "@mobile": {
    fontSize: "13px !important",
  },
  border: "0.5px solid black",
  color: "$text",
  [`.${darkTheme.className} &`]: {
    border: "0.5px solid $border",
  },
  borderRadius: "18px",
  height: "30px !important",
  display: "flex",
  alignItems: "center",
  textAlign: "center",
  width: "230px",
  cursor: "pointer",
  position: "relative",
  justifyContent: "center",
  "& .menu, & .tz-input-container": {
    display: "none",
  },
  // Downward "carrot" affordance on the right edge of the pill. The asset is a
  // right-pointing chevron, rotated 90deg to point down. Hidden while open so
  // it never sits over the search input.
  "& .dropdown-caret": {
    position: "absolute",
    right: "14px",
    top: "50%",
    height: "8px",
    width: "auto",
    transform: "translateY(-50%) rotate(90deg)",
    pointerEvents: "none",
    [`.${darkTheme.className} &`]: {
      filter: "invert(1)",
    },
  },
  "&.open": {
    "& .dropdown-caret": {
      display: "none",
    },
    "& .tz-input-container": {
      display: "block",
      backgroundColor: "white",
      [`.${darkTheme.className} &`]: {
        backgroundColor: "$surface",
      },
      position: "absolute",
      width: "100%",
      zIndex: "99",
      top: "-9px",
      paddingTop: "10px",
      paddingBottom: "0 !important",
      left: 0,
      right: 0,
      borderRadius: "25px 25px 0 0",
    },
    "& input": {
      textAlign: "center",
      width: "230px",
      padding: "8px 16px",
      borderRadius: "25px !important",
      marginBottom: "4px",
      fontSize: "12px !important",
      "@mobile": {
        fontSize: "14px !important",
      },
      outline: "none",
      border: "none",
      height: "30px !important",
      [`.${darkTheme.className} &`]: {
        backgroundColor: "$inputBg",
        color: "$text",
      },
      "&:focus": {
        outline: "1px solid black",
        border: "none",
        [`.${darkTheme.className} &`]: {
          outline: "1px solid $border",
        },
      },
      // The input is focused when the menu opens, so the placeholder must
      // survive focus or it would never be seen. Browsers already clear it as
      // soon as the user types.
      "&::placeholder": {
        fontSize: "11px",
        color: "$gray400",
      },
      "&:not(:focus)": {
        outline: "0.5px solid $gray400",
      },
    },
    "& .menu": {
      display: "block",
      position: "absolute",
      zIndex: "10",
      height: "200px",
      overflow: "hidden",
      backgroundColor: "white",
      border: "1px solid black",
      [`.${darkTheme.className} &`]: {
        backgroundColor: "$surface",
        border: "1px solid $border",
      },
      left: "-16px",
      top: "-10px",
      width: "calc(100% + 30px)",
      borderRadius: "25px",
      paddingTop: "48px",
      "& .inner-menu": {
        // `auto` so the scrollbar only appears when the list overflows.
        overflowY: "auto",
        height: "100%",
        // Modern browsers ignore the ::-webkit-scrollbar rules below whenever
        // scrollbar-width is set, so the colors have to be set here too.
        scrollbarWidth: "thin",
        scrollbarColor: "rgba(0, 0, 0, 0.3) transparent",
        [`.${darkTheme.className} &`]: {
          scrollbarColor: "rgba(255, 255, 255, 0.3) transparent",
          colorScheme: "dark",
        },
        "&::-webkit-scrollbar": {
          width: "6px",
        },
        "&::-webkit-scrollbar-track": {
          background: "transparent",
        },
        "&::-webkit-scrollbar-thumb": {
          borderRadius: "20px",
          backgroundColor: "rgba(0, 0, 0, 0.3)",
          [`.${darkTheme.className} &`]: {
            backgroundColor: "rgba(255, 255, 255, 0.3)",
          },
        },
      },

      "& .menu-item": {
        fontSize: "10px",
        "@mobile": {
          fontSize: "13px",
        },

        marginTop: "2px",
        marginBottom: "2px",
        padding: "8px 16px",
        "&:hover": {
          backgroundColor: "$gray200",
          [`.${darkTheme.className} &`]: {
            backgroundColor: "$subtleFill",
          },
        },
      },
    },
  },
  "&.align-left .menu-item": {
    textAlign: "left",
  },
  // Match the search input to the list it filters: centered text would put the
  // caret mid-pill, away from the left-aligned results below it.
  "&.align-left.open input": {
    textAlign: "left",
  },
  // Opt-in: fill the parent's width instead of the fixed 230px, so the pill can
  // line up with a full-width button. Overrides the hardcoded widths on the
  // container and (when open) the search input.
  "&.full-width": {
    width: "218px",
    // Symmetric horizontal padding leaves room for the caret on the right and
    // keeps the (centered) label visually balanced without overlapping it.
    padding: "0 24px",
    "&.open input": {
      width: "218px",
    },
  },
});
